import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, X } from 'lucide-react';
interface PlayProject {
  title: string;
  tag: string;
  description: string;
  imageUrl: string;
  bgColor: string;
  links: {
    label: string;
    url: string;
  }[];
}
const projects: PlayProject[] = [{
  title: 'My Ottawa Recommendations',
  tag: 'TOOL',
  description: "When friends from out of town visit Ottawa, I'm often sending them a long list & I find google map lists boring so I made a website I can easily share.",
  imageUrl: 'https://cdn.magicpatterns.com/uploads/chTWXZBEGdXSL7DZsmV9oH/Ottawa_reccos.png',
  bgColor: '#2D8B7A',
  links: [{
    label: 'Visit site',
    url: 'https://project-3d-isometric-city-map-478.magicpatterns.app'
  }]
}, {
  title: 'Jewelry Taste Curator',
  tag: 'TOOL',
  description: 'A website I created for my wife to find out what jewelry she likes.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/mvRSEb41dsh5tgUNaqcKwg/image.png',
  bgColor: '#1B2621',
  links: [{
    label: 'Visit site',
    url: '#'
  }]
}, {
  title: 'Random Task Picker',
  tag: 'TOOL',
  description: 'An app I created in Wabi to help conquer analysis paralysis.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/22sZbNwJaSLVjarmV4CsfG/to_do.png',
  bgColor: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
  links: [{
    label: 'Try it',
    url: 'https://wabi.ai/@jp_davidson/task-picker-1003997?_v=3'
  }]
}, {
  title: 'Cartoons',
  tag: 'ART',
  description: "Cartoons and doodles I've done since 2019.",
  imageUrl: 'https://cdn.magicpatterns.com/uploads/eZLZMfpgf769bD4nhzPTUi/Untitled_Artwork_6.png',
  bgColor: '#D0D5DA',
  links: [{
    label: 'Instagram',
    url: '#'
  }]
}, {
  title: 'Wedding Decoration Kit',
  tag: 'SITE',
  description: 'A Figma file used by 1.5k users that contains signage, thank you cards, table numbers, and much more.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/qZbUvvSzfEa7bwHqjnSYYp/file_cover_-_2.png',
  bgColor: 'linear-gradient(135deg, #E8C4B8 0%, #D4A99A 100%)',
  links: [{
    label: 'Figma link',
    url: 'https://www.figma.com/community/file/1286127891389738978/wedding-decoration-starter-kit'
  }]
}, {
  title: 'Garbage SMS',
  tag: 'UTILITY',
  description: "Ottawa's official garbage collection app required too many taps and manual check-ins to stay on top of weekly pickup schedules. To solve this, I built a lightweight Python automation that parses my address-specific iCal feed from the City of Ottawa and sends a personalized SMS reminder the night before each pickup via the Twilio API. The script runs automatically every evening via a cron job, requiring zero ongoing effort. The result is a dead-simple, always-on reminder that tells me exactly which bins to put out, without ever opening an app.",
  imageUrl: 'https://cdn.magicpatterns.com/uploads/sBTSQpXYJMGAtGbzyt5gY4/garbage.png',
  bgColor: '#E8E4E0',
  links: []
}];
function PlayCard({
  project,
  index,
  onClick,
  className = ''
}: {
  project: PlayProject;
  index: number;
  onClick: () => void;
  className?: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 40
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.6,
    ease: 'easeOut',
    delay: index * 0.08
  }} className={className} data-id="element-694">
      <motion.div onClick={onClick} className="group relative rounded-2xl overflow-hidden cursor-pointer h-full min-h-[200px] md:min-h-[240px]" style={{
      background: project.bgColor
    }} whileHover={{
      y: -4,
      boxShadow: '0 12px 32px rgba(0,0,0,0.15)'
    }} transition={{
      type: 'tween',
      duration: 0.2,
      ease: 'easeOut'
    }} data-id="element-695">
        {/* Background image for Ottawa card */}
        {index === 0 && <img src="https://cdn.magicpatterns.com/uploads/p3nGxUFP7Y7XUATR9npaP3/image.png" alt="" className="absolute -inset-px w-[calc(100%+2px)] h-[calc(100%+2px)] object-cover" data-id="element-696" />}

        {/* Image */}
        {index === 0 ? <div className="absolute top-4 right-6 flex items-start pointer-events-none" data-id="element-697">
            <img src="https://cdn.magicpatterns.com/uploads/b8663tcbAqr2HUsfZdiJTj/image.png" alt="Ottawa landmark 1" className="w-[72px] md:w-[88px] object-contain rounded-md transition-transform duration-700 ease-[cubic-bezier(0.23,1,0.32,1)] -rotate-6 group-hover:-rotate-[10deg] relative z-10" data-id="element-698" />
            <img src="https://cdn.magicpatterns.com/uploads/21hmCkuocGCtMMH5cz93zP/image.png" alt="Ottawa landmark 2" className="w-[72px] md:w-[88px] object-contain rounded-md transition-transform duration-700 ease-[cubic-bezier(0.23,1,0.32,1)] rotate-[4deg] group-hover:rotate-[8deg] -ml-5 mt-3" data-id="element-699" />
          </div> : index === 5 ? <div className="absolute inset-0 flex items-start justify-center pointer-events-none p-4 pt-4" data-id="element-700">
            <img src="https://cdn.magicpatterns.com/uploads/kaGhi96if8BaTEchcc3sBJ/image.png" alt="Garbage SMS screenshot" className="max-h-[60%] w-auto object-contain rounded-xl" style={{
          filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.15))'
        }} data-id="element-701" />
          </div> : index === 2 ? <div className="absolute -bottom-4 -right-4 pointer-events-none" data-id="element-702">
            <img src="https://cdn.magicpatterns.com/uploads/2Cd58WcSqaapG3pHx8BYMZ/image.png" alt="Task Picker app screenshot" className="w-[45%] md:w-[60%] max-w-none object-contain ml-auto transition-transform duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:scale-[1.15]" style={{
          filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.2))'
        }} data-id="element-703" />
          </div> : index === 3 ? <>
            {/* Background image */}
            <img src="https://cdn.magicpatterns.com/uploads/uZkGfV1QdWPqD2w3oh2faD/image.png" alt="" className="absolute -inset-px w-[calc(100%+2px)] h-[calc(100%+2px)] object-cover" data-id="element-704" />
          </> : index === 4 ? <div className="absolute top-0 right-0 bottom-0 w-[60%] md:w-[55%] md:right-4 md:left-auto md:mx-0 pointer-events-none overflow-hidden max-md:left-1/2 max-md:-translate-x-1/2 max-md:w-[70%] max-[640px]:w-[50%] max-[640px]:top-0 max-[640px]:bottom-auto max-[640px]:h-[55%] max-[640px]:left-1/2 max-[640px]:-translate-x-1/2" data-id="element-705">
            <img src="https://cdn.magicpatterns.com/uploads/cShRu27LpshUGpW1waer6R/image.png" alt="Wedding decoration kit preview" className="w-full object-contain object-top translate-y-0 transition-transform duration-[1.8s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-[20%] rounded-lg max-[640px]:group-hover:-translate-y-0" style={{
          filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.15))'
        }} data-id="element-706" />
          </div> : project.imageUrl && <div className={`absolute inset-0 flex items-center justify-end ${index === 1 ? '' : 'p-4'}`} data-id="element-707">
              <img src={project.imageUrl} alt={project.title} className={`object-contain rounded-lg ${index === 1 ? 'w-[49%] max-w-none transition-transform duration-700 ease-[cubic-bezier(0.23,1,0.32,1)] translate-x-[10%] -translate-y-[35%] group-hover:translate-x-[8%] group-hover:-translate-y-[38%] group-hover:-rotate-[4deg]' : 'max-h-[80%] max-w-[60%] opacity-90'}`} style={{
          filter: 'drop-shadow(0 4px 24px rgba(255,255,255,0.15))'
        }} data-id="element-708" />
            </div>}

        {/* Overlay gradient for text readability */}
        <div className={`absolute inset-0 ${index === 0 ? 'bg-gradient-to-t from-black/60 via-black/20 to-transparent' : index === 5 ? 'bg-gradient-to-t from-[#E8E4E0] via-[#E8E4E0]/80 via-[35%] to-transparent' : index === 3 ? 'bg-gradient-to-t from-[#C8CDD2]/70 via-transparent to-transparent' : index === 2 ? '' : 'bg-gradient-to-t from-black/40 via-transparent to-transparent'}`} data-id="element-709" />

        {/* Text overlay */}
        <div className={`absolute p-5 ${index === 2 ? 'top-0 left-0' : 'bottom-0 left-0 right-0'}`} data-id="element-710">
          <h3 className={`text-xl font-bold leading-snug mb-2 ${index === 5 || index === 3 ? 'text-[#1A1A1A]' : 'text-white'}`} data-id="element-711">
            {index === 3 ? <span className="relative inline-block" data-id="element-712">
                {project.title}
                <svg className="absolute -inset-x-[12px] -inset-y-[8px] w-[calc(100%+24px)] h-[calc(100%+16px)] pointer-events-none" viewBox="0 0 100 50" fill="none" preserveAspectRatio="none" ref={el => {
              if (!el) return;
              const path = el.querySelector('path');
              const card = el.closest('.group');
              if (!card || !path) return;
              let drawn = false;
              const len = path.getTotalLength();
              path.style.strokeDasharray = String(len);
              path.style.strokeDashoffset = String(len);
              path.style.transition = 'none';
              const show = () => {
                if (drawn) return;
                drawn = true;
                path.style.transition = 'stroke-dashoffset 0.5s cubic-bezier(0.25,0.1,0.25,1)';
                path.style.strokeDashoffset = '0';
              };
              card.addEventListener('mouseenter', show);
            }} data-id="element-713">
                  <path d="M 50 2 C 72 -1, 96 6, 97 22 C 98 38, 78 48, 50 48 C 22 48, 2 40, 3 24 C 4 8, 28 1, 50 2 Z" stroke="#1A1A1A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" data-id="element-714" />
                </svg>
              </span> : project.title}
          </h3>
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${index === 5 || index === 3 ? 'bg-[#1A1A1A]/10 text-[#6B6560]' : 'bg-white/20 backdrop-blur-sm text-white'}`} data-id="element-715">
            {project.tag}
          </span>
        </div>
      </motion.div>
    </motion.div>;
}
export function PlayPage() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const selectedProject = selectedIndex !== null ? projects[selectedIndex] : null;
  const springTransition = {
    type: 'spring',
    damping: 25,
    stiffness: 300,
    mass: 1
  };
  return <div className="min-h-screen bg-[#F8F6F3] text-[#1A1A1A] overflow-hidden" data-id="element-716">
      {/* Main Content */}
      <motion.div animate={{
      opacity: selectedIndex !== null ? 0.4 : 1,
      filter: selectedIndex !== null ? 'blur(4px)' : 'blur(0px)'
    }} transition={springTransition} className="max-w-7xl mx-auto px-8 md:px-16 lg:px-24 py-12 md:py-20" data-id="element-717">
        {/* Header */}
        <div className="mb-12" data-id="element-718">
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] leading-snug mb-4" data-id="element-719">
            Play
          </h1>
          <p className="text-base md:text-lg text-[#6B6560] max-w-2xl" data-id="element-720">
            Experiments, side projects, and creative explorations.
          </p>
        </div>

        {/* Card Grid — Custom layout */}
        <style data-id="element-721">{`
            @media (min-width: 1024px) {
              .play-grid {
                grid-template-columns: 1fr 1fr 1fr 1fr !important;
                grid-template-rows: auto auto auto;
                grid-template-areas:
                  "ottawa ottawa jewelry jewelry"
                  "picker cartoons jewelry jewelry"
                  "wedding wedding wedding garbage";
              }
              .play-grid .card-ottawa { grid-area: ottawa; }
              .play-grid .card-jewelry { grid-area: jewelry; }
              .play-grid .card-picker { grid-area: picker; }
              .play-grid .card-cartoons { grid-area: cartoons; }
              .play-grid .card-wedding { grid-area: wedding; }
              .play-grid .card-garbage { grid-area: garbage; }
            }
          `}</style>
        <div className="play-grid grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-722">
          <PlayCard project={projects[0]} index={0} onClick={() => setSelectedIndex(0)} className="card-ottawa" data-id="element-723" />
          <PlayCard project={projects[1]} index={1} onClick={() => setSelectedIndex(1)} className="card-jewelry" data-id="element-724" />
          <PlayCard project={projects[2]} index={2} onClick={() => setSelectedIndex(2)} className="card-picker" data-id="element-725" />
          <PlayCard project={projects[3]} index={3} onClick={() => setSelectedIndex(3)} className="card-cartoons" data-id="element-726" />
          <PlayCard project={projects[4]} index={4} onClick={() => setSelectedIndex(4)} className="card-wedding" data-id="element-727" />
          <PlayCard project={projects[5]} index={5} onClick={() => setSelectedIndex(5)} className="card-garbage" data-id="element-728" />
        </div>
      </motion.div>

      {/* Modal */}
      <AnimatePresence data-id="element-729">
        {selectedIndex !== null && selectedProject && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" data-id="element-730">
            {/* Backdrop */}
            <motion.div className="absolute inset-0 bg-black/20" onClick={() => setSelectedIndex(null)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-731" />

            {/* Modal content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={springTransition} className={`relative z-10 bg-white rounded-2xl w-full shadow-2xl ${selectedIndex === 2 || selectedIndex === 5 ? 'max-w-4xl flex flex-col md:flex-row max-h-[90vh] overflow-y-auto scrollbar-hide' : 'max-w-2xl flex flex-col overflow-hidden'}`} data-id="element-732">
              {/* Close button */}
              <button onClick={() => setSelectedIndex(null)} className={selectedIndex === 2 || selectedIndex === 5 ? 'sticky top-0 z-20 self-end md:absolute md:top-4 md:right-4 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-[#1A1A1A] hover:bg-white transition-colors m-3 md:m-0 shrink-0' : 'absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-[#1A1A1A] hover:bg-white transition-colors'} aria-label="Close" data-id="element-733">
                <X size={18} data-id="element-734" />
              </button>

              {/* Media */}
              <div className={`relative overflow-hidden ${selectedIndex === 2 || selectedIndex === 5 ? 'w-full md:w-[360px] shrink-0 bg-[#F8F6F3] flex items-center justify-center' : 'w-full aspect-[16/9]'}`} data-id="element-735">
                {selectedIndex === 2 ? <div className="w-full p-4" data-id="element-736">
                    <video src="https://cdn.magicpatterns.com/uploads/tmghkfeKSDR1Hbz9rizaL4/ScreenRecording_03-09-2026 09-49-50_1.MP4" className="w-full h-auto object-contain rounded-[16px]" autoPlay loop muted playsInline data-id="element-737" />
                  </div> : selectedIndex === 5 ? <div className="w-full h-full p-4" data-id="element-738">
                    <img src={selectedProject.imageUrl} alt={selectedProject.title} className="w-full h-full object-contain rounded-[12px]" data-id="element-739" />
                  </div> : selectedProject.imageUrl ? <>
                    <img src={selectedProject.imageUrl} alt={selectedProject.title} className="w-full h-full object-cover" data-id="element-740" />
                    <div className="absolute inset-0 img-outline pointer-events-none" data-id="element-741" />
                  </> : <div className="w-full h-full bg-[#D5D0CB]" data-id="element-742" />}
              </div>

              {/* Content */}
              <div className={`p-8 ${selectedIndex === 2 || selectedIndex === 5 ? 'flex flex-col justify-center flex-grow' : ''}`} data-id="element-743">
                <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" data-id="element-744">
                  {selectedProject.title}
                </h2>
                <div className="mb-4" data-id="element-745">
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 bg-[#F0EDEA] text-[#6B6560] rounded-full" data-id="element-746">
                    {selectedProject.tag}
                  </span>
                </div>
                <p className="text-base text-[#6B6560] leading-relaxed mb-6" data-id="element-747">
                  {selectedProject.description}
                </p>

                {selectedIndex !== 5 && selectedProject.links.length > 0 && <div className="flex items-center gap-4" data-id="element-748">
                    {selectedProject.links.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#1A1A1A] text-white text-sm font-medium rounded-lg hover:bg-[#333] transition-colors ${selectedIndex === 2 ? 'w-full' : ''}`} data-id="element-749">
                        {link.label}
                        <ExternalLink className="w-4 h-4" data-id="element-750" />
                      </a>)}
                  </div>}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}